import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import events from '../data/events';

// Printable monthly calendars, newest first. Each PDF is English on page 1 and
// Spanish on page 2. To add a month: drop the PDF in public/assets/images and
// add an entry at the top of this list.
const monthlyCalendars = [
  { label: 'October 2026', labelES: 'Octubre 2026', href: '/assets/images/October_2026_Calendar.pdf' },
  { label: 'September 2026', labelES: 'Septiembre 2026', href: '/assets/images/September_2026_Calendar.pdf' },
];

const Calendar = () => {
  const [currentView, setCurrentView] = useState('month');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedFilters, setSelectedFilters] = useState({
    holiday: true,
    academic: true,
    community: true,
    spirit: true,
    enrichment: true,
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [language, setLanguage] = useState('en');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const modalRef = useRef(null);
  const triggerRef = useRef(null);
  const liveRegionRef = useRef(null);

  // --- Color config ---

  const categoryColors = {
    holiday: 'bg-red-600',
    academic: 'bg-school-blue',
    community: 'bg-teal-700',
    spirit: 'bg-school-gold',
    enrichment: 'bg-purple-600',
  };

  const categoryTextColors = {
    holiday: 'text-white',
    academic: 'text-white',
    community: 'text-white',
    // school-gold (#C59132) fails 4.5:1 with white text (2.81:1); school-blue
    // was tuned specifically for AA contrast on gold backgrounds (5.27:1).
    spirit: 'text-school-blue',
    enrichment: 'text-white',
  };

  const categoryColorValues = {
    holiday: '#DC2626',
    academic: '#1B365D',
    community: '#0F766E',
    spirit: '#C59132',
    enrichment: '#9333EA',
  };

  // --- Date helpers ---

  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDay = new Date(year, month, 1).getDay();
    for (let i = 0; i < firstDay; i++) days.push(null);
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getWeekDays = () => {
    const base = new Date(currentYear, currentMonth, 1);
    const start = getStartOfWeek(base);
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + currentWeekOffset * 7);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return { weekStart, days };
  };

  const dayInRange = (day, start, end) => {
    const d = new Date(day); d.setHours(0, 0, 0, 0);
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end); e.setHours(0, 0, 0, 0);
    return d >= s && d <= e;
  };

  // --- Event helpers ---

  const getFilteredEvents = () => events.filter((event) => selectedFilters[event.category]);

  const getUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return getFilteredEvents()
      .filter((e) => {
        const end = e.endDate ? new Date(e.endDate) : new Date(e.date);
        end.setHours(23, 59, 59, 999);
        return end >= today;
      })
      .sort((a, b) => a.date - b.date)
      .slice(0, 5);
  };

  const getEventsForDay = (day) => {
    return events.filter((event) => {
      if (!selectedFilters[event.category]) return false;
      const eventStart = new Date(event.date);
      const eventEnd = event.endDate ? new Date(event.endDate) : eventStart;
      return dayInRange(day, eventStart, eventEnd);
    });
  };

  const getCategoryName = (category) => {
    const names = {
      holiday: 'Holiday',
      academic: 'Academic',
      community: 'Community',
      spirit: 'Spirit Day',
      enrichment: 'Enrichment',
    };
    return names[category] || category;
  };

  const getCategoryNameES = (category) => {
    const names = {
      holiday: 'Feriado',
      academic: 'Académico',
      community: 'Comunidad',
      spirit: 'Día de Espíritu',
      enrichment: 'Enriquecimiento',
    };
    return names[category] || category;
  };

  const formatDate = (dateObj) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateObj).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  const formatDateRange = (event) => {
    const locale = language === 'es' ? 'es-ES' : 'en-US';
    if (!event.endDate) return formatDate(event.date);
    const start = new Date(event.date).toLocaleDateString(locale, { month: 'long', day: 'numeric' });
    const end = new Date(event.endDate).toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
    return `${start} \u2013 ${end}`;
  };

  const getEventTitle = (event) => (language === 'es' && event.titleES ? event.titleES : event.title);
  const getEventDescription = (event) => (language === 'es' && event.descriptionES ? event.descriptionES : event.description || '');

  const toggleFilter = (filter) => {
    setSelectedFilters({ ...selectedFilters, [filter]: !selectedFilters[filter] });
  };

  const getTimeDisplay = (event) => {
    if (event.allDay) return language === 'es' ? 'Todo el día' : 'All Day';
    return `${event.startTime || ''}${event.startTime && event.endTime ? ' - ' : ''}${event.endTime || ''}`;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const monthNamesES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  const dayNamesShort = language === 'es'
    ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const dayNamesFull = language === 'es'
    ? ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
    : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const getMonthLabel = () => {
    const names = language === 'es' ? monthNamesES : monthNames;
    return `${names[currentMonth]} ${currentYear}`;
  };

  // --- Navigation ---

  const announce = (message) => {
    if (liveRegionRef.current) liveRegionRef.current.textContent = message;
  };

  const prevMonth = () => {
    if (currentView === 'week') {
      setCurrentWeekOffset(currentWeekOffset - 1);
      return;
    }
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentView === 'week') {
      setCurrentWeekOffset(currentWeekOffset + 1);
      return;
    }
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  useEffect(() => {
    if (currentView === 'week') {
      const { days: wd } = getWeekDays();
      announce(`Week of ${wd[0].toLocaleString('default', { month: 'long' })} ${wd[0].getDate()} to ${wd[6].toLocaleString('default', { month: 'long' })} ${wd[6].getDate()}, ${wd[6].getFullYear()}`);
    } else {
      announce(`${monthNames[currentMonth]} ${currentYear}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear, currentWeekOffset, currentView]);

  // --- Modal accessibility ---

  const openEventModal = (event, buttonEl) => {
    triggerRef.current = buttonEl || document.activeElement;
    setSelectedEvent(event);
  };

  const closeModal = useCallback(() => {
    setSelectedEvent(null);
    if (triggerRef.current && triggerRef.current.focus) {
      requestAnimationFrame(() => triggerRef.current.focus());
    }
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEvent, closeModal]);

  useEffect(() => {
    if (!selectedEvent || !modalRef.current) return;
    const modal = modalRef.current;
    const focusable = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable.length > 0) focusable[0].focus();
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    modal.addEventListener('keydown', handleTab);
    return () => modal.removeEventListener('keydown', handleTab);
  }, [selectedEvent]);

  // --- Computed ---

  const days = getDaysInMonth(currentMonth, currentYear);
  const upcomingEvents = getUpcomingEvents();

  const filterOptions = [
    { key: 'holiday', label: language === 'es' ? 'Feriados' : 'Holidays', color: '#DC2626' },
    { key: 'academic', label: language === 'es' ? 'Eventos Académicos' : 'Academic Events', color: '#1B365D' },
    { key: 'community', label: language === 'es' ? 'Comunidad y Padres' : 'Community & Parents', color: '#0D9488' },
    { key: 'spirit', label: language === 'es' ? 'Días de Espíritu' : 'Spirit Days', color: '#C59132' },
    { key: 'enrichment', label: language === 'es' ? 'Enriquecimiento' : 'Enrichment', color: '#9333EA' },
  ];

  return (
    <div
      className="min-h-screen font-sans text-gray-800 selection:bg-school-gold/30 selection:text-school-blue"
      style={{ backgroundColor: '#F5EFE0' }}
    >
      <Navbar />

      {/* Screen reader live region */}
      <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="sr-only" />

      <main id="main-content">

        {/* ==================== HERO ==================== */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" style={{ backgroundColor: '#0F2240' }}>
          <div className="absolute inset-0">
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-school-gold/12 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -left-10 bottom-0 w-60 h-60 bg-school-gold/6 rounded-full blur-2xl pointer-events-none" />
          </div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-sm rounded-full mb-8 border border-white/20">
              <CalendarDaysIcon className="w-8 h-8 text-school-gold" aria-hidden="true" />
            </div>
            <span className="text-school-gold font-bold tracking-[0.2em] uppercase text-xs mb-4 block">
              {language === 'es' ? 'Mantente Informado' : 'Stay Informed'}
            </span>
            <h1
              className="font-heading font-bold text-white tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              {language === 'es' ? 'Calendario ' : 'Family '}
              <em className="text-school-gold font-light not-italic">
                {language === 'es' ? 'Familiar' : 'Calendar'}
              </em>
            </h1>
            <p
              className="text-white/55 font-light leading-relaxed max-w-xl mx-auto"
              style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}
            >
              {language === 'es'
                ? 'Mantente al día con los eventos escolares, feriados y fechas importantes del año escolar.'
                : 'Stay up to date with school events, holidays, and important dates throughout the academic year.'}
            </p>

            {/* Printable monthly calendars, newest first: the newest is the
                prominent button, earlier months sit beside it outlined. */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {monthlyCalendars.map((cal, i) => (
                <a
                  key={cal.href}
                  href={cal.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-3 px-7 py-3.5 rounded-full font-bold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-school-gold focus:ring-offset-2 focus:ring-offset-[#0F2240] ${
                    i === 0
                      ? 'bg-school-gold text-school-blue hover:bg-white'
                      : 'bg-[#0F2240] text-white border-2 border-white/30 hover:bg-white hover:text-school-blue'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  {language === 'es' ? `Calendario de ${cal.labelES} (PDF)` : `${cal.label} Calendar (PDF)`}
                  <span className="sr-only">
                    {language === 'es' ? ' (se abre en una ventana nueva)' : ' (opens in new window)'}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ==================== CALENDAR SECTION ==================== */}
        <section className="py-16" aria-label="Calendar" style={{ backgroundColor: '#F5EFE0' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Mobile Filter Toggle */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl shadow-sm border border-gray-200 text-sm font-semibold text-gray-900 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-school-blue/40"
                aria-expanded={mobileFiltersOpen}
                aria-controls="calendar-sidebar"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
                  </svg>
                  {language === 'es' ? 'Filtros y Opciones' : 'Filters & Options'}
                </span>
                <svg className={`w-5 h-5 text-gray-600 transition-transform ${mobileFiltersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

              {/* ========== SIDEBAR ========== */}
              <aside id="calendar-sidebar" className={`lg:col-span-1 ${mobileFiltersOpen ? 'block' : 'hidden'} lg:block`} aria-label="Calendar filters">
                <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/40 border border-gray-100 p-6 h-fit sticky top-28">
                  <fieldset>
                    <legend className="text-lg font-heading font-bold text-school-blue mb-4">
                      {language === 'es' ? 'Filtrar Eventos' : 'Filter Events'}
                    </legend>
                    <div className="space-y-3">
                      {filterOptions.map((f) => (
                        <div key={f.key} className="flex items-center">
                          <input
                            id={`filter-${f.key}`}
                            type="checkbox"
                            checked={selectedFilters[f.key]}
                            onChange={() => toggleFilter(f.key)}
                            className="h-5 w-5 rounded border-gray-300 text-school-blue focus:ring-school-blue/40"
                          />
                          <label htmlFor={`filter-${f.key}`} className="ml-2 flex items-center text-gray-700 text-sm cursor-pointer">
                            <span className="h-3 w-3 rounded-full mr-2 shrink-0" style={{ backgroundColor: f.color }} aria-hidden="true" />
                            {f.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  {/* Language toggle */}
                  <div className="mt-8" role="group" aria-label="Language">
                    <h2 className="text-sm font-bold text-school-blue mb-3">
                      {language === 'es' ? 'Idioma' : 'Language'}
                    </h2>
                    <div className="flex space-x-2">
                      {[
                        { code: 'en', label: 'English', short: 'EN' },
                        { code: 'es', label: 'Español', short: 'ES' },
                      ].map((l) => (
                        <button
                          key={l.code}
                          onClick={() => setLanguage(l.code)}
                          className={`px-4 py-2 text-xs font-bold rounded-full min-h-[36px] focus:outline-none focus:ring-2 focus:ring-school-blue/40 transition-colors ${
                            language === l.code
                              ? 'bg-school-blue text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          aria-pressed={language === l.code}
                          aria-label={l.label}
                        >
                          {l.short}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* View options */}
                  <div className="mt-8" role="group" aria-label="View options">
                    <h2 className="text-sm font-bold text-school-blue mb-3">
                      {language === 'es' ? 'Vista' : 'View'}
                    </h2>
                    <div className="flex space-x-2">
                      {[
                        { key: 'month', label: language === 'es' ? 'Mes' : 'Month' },
                        { key: 'week', label: language === 'es' ? 'Semana' : 'Week' },
                        { key: 'list', label: language === 'es' ? 'Lista' : 'List' },
                      ].map((v) => (
                        <button
                          key={v.key}
                          onClick={() => {
                            setCurrentView(v.key);
                            if (v.key === 'week') setCurrentWeekOffset(0);
                          }}
                          className={`px-4 py-2 text-xs font-bold rounded-full min-h-[36px] focus:outline-none focus:ring-2 focus:ring-school-blue/40 transition-colors ${
                            currentView === v.key
                              ? 'bg-school-blue text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          aria-pressed={currentView === v.key}
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        setCurrentMonth(new Date().getMonth());
                        setCurrentYear(new Date().getFullYear());
                        setCurrentWeekOffset(0);
                      }}
                      className="w-full mt-3 py-2.5 min-h-[40px] border-2 border-school-blue text-school-blue rounded-full hover:bg-school-blue hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-school-blue/40 text-sm font-bold"
                    >
                      {language === 'es' ? 'Hoy' : 'Today'}
                    </button>
                  </div>

                  {/* Upcoming events */}
                  <div className="mt-8">
                    <h2 className="text-sm font-bold text-school-blue mb-3">
                      {language === 'es' ? 'Próximos Eventos' : 'Upcoming Events'}
                    </h2>
                    <div className="space-y-3">
                      {upcomingEvents.length > 0 ? (
                        upcomingEvents.map((event) => (
                          <button
                            key={event.id}
                            onClick={(e) => openEventModal(event, e.currentTarget)}
                            className="w-full text-left border-l-4 pl-3 py-2 min-h-[44px] hover:bg-gray-50 rounded-r transition-colors focus:outline-none focus:ring-2 focus:ring-school-blue/40"
                            style={{ borderColor: categoryColorValues[event.category] }}
                            aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}`}
                          >
                            <div className="text-xs text-gray-500">{event.endDate ? formatDateRange(event) : formatDate(event.date)}</div>
                            <div className="text-sm font-bold text-gray-900">{getEventTitle(event)}</div>
                            <div className="text-xs text-gray-600">{getTimeDisplay(event)}</div>
                          </button>
                        ))
                      ) : (
                        <p className="text-xs text-gray-600 italic">
                          {language === 'es' ? 'No hay próximos eventos según tus filtros.' : 'No upcoming events based on your filters.'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </aside>

              {/* ========== MAIN CALENDAR ========== */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/40 border border-gray-100 overflow-hidden">
                  {/* Calendar header */}
                  <div className="bg-school-blue text-white px-6 py-4 flex items-center justify-between">
                    <div>
                      {currentView === 'week' ? (
                        (() => {
                          const { days: wd } = getWeekDays();
                          const locale = language === 'es' ? 'es-ES' : 'en-US';
                          const startLabel = `${wd[0].toLocaleString(locale, { month: 'short' })} ${wd[0].getDate()}`;
                          const endLabel = `${wd[6].toLocaleString(locale, { month: 'short' })} ${wd[6].getDate()}, ${wd[6].getFullYear()}`;
                          return (
                            <h2 className="text-base font-heading font-bold text-white" id="calendar-heading">
                              {language === 'es' ? 'Semana del' : 'Week of'} {startLabel} \u2013 {endLabel}
                            </h2>
                          );
                        })()
                      ) : (
                        <h2 className="text-base font-heading font-bold text-white" id="calendar-heading">{getMonthLabel()}</h2>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={prevMonth}
                        className="p-2 min-h-[36px] min-w-[36px] flex items-center justify-center rounded-full hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
                        aria-label={language === 'es' ? 'Anterior' : 'Previous'}
                      >
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        onClick={nextMonth}
                        className="p-2 min-h-[36px] min-w-[36px] flex items-center justify-center rounded-full hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
                        aria-label={language === 'es' ? 'Siguiente' : 'Next'}
                      >
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* ===== MONTH VIEW ===== */}
                  {currentView === 'month' && (
                    <div className="grid grid-cols-7 auto-rows-fr border-b border-gray-200 min-h-[560px]" role="grid" aria-labelledby="calendar-heading">
                      <div role="row" className="contents bg-gray-50 border-b border-gray-200" style={{ display: 'contents' }}>
                        {dayNamesShort.map((day, i) => (
                          <div key={`hd-${i}`} className="py-2 text-xs font-bold text-school-blue text-center bg-gray-50 border-b border-gray-200" role="columnheader">
                            <abbr title={dayNamesFull[i]} className="no-underline">{day}</abbr>
                          </div>
                        ))}
                      </div>
                      {Array.from({ length: Math.ceil(days.length / 7) }, (_, weekIndex) => (
                        <div key={`week-${weekIndex}`} role="row" style={{ display: 'contents' }}>
                          {days.slice(weekIndex * 7, weekIndex * 7 + 7).map((day, idxInRow) => {
                            const index = weekIndex * 7 + idxInRow;
                            const dayEvents = day ? getEventsForDay(day) : [];
                            const isToday = day && day.toDateString() === new Date().toDateString();
                            const dateLabel = day
                              ? day.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                              : '';
                            return (
                              <div
                                key={index}
                                role="gridcell"
                                aria-label={day ? `${dateLabel}${dayEvents.length > 0 ? `, ${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}` : ''}` : undefined}
                                className={`border-t border-l p-1.5 sm:p-2 min-h-[80px] sm:h-32 ${day ? 'bg-white' : 'bg-gray-50/50'} ${
                                  isToday ? 'bg-school-gold/10 ring-2 ring-inset ring-school-gold' : ''
                                } ${index % 7 === 6 ? 'border-r' : ''}`}
                              >
                                {day && (
                                  <>
                                    <div className={`text-right mb-1 text-xs ${isToday ? 'font-bold text-school-blue' : 'text-gray-600'}`}>
                                      {day.getDate()}
                                    </div>
                                    <div className="space-y-0.5 sm:space-y-1 overflow-y-auto max-h-[50px] sm:max-h-[80px]">
                                      {dayEvents.map((event) => (
                                        <button
                                          key={event.id}
                                          onClick={(e) => openEventModal(event, e.currentTarget)}
                                          className={`text-[10px] sm:text-[11px] w-full text-left px-1.5 sm:px-2 min-h-[20px] py-0.5 sm:py-1 rounded ${categoryColors[event.category]} ${categoryTextColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-school-blue`}
                                          title={getEventTitle(event)}
                                          aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}`}
                                        >
                                          <span className="truncate block">{getEventTitle(event)}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ===== WEEK VIEW ===== */}
                  {currentView === 'week' && (() => {
                    const { days: weekDays } = getWeekDays();
                    return (
                      <div className="border-b border-gray-200 min-h-[360px]">
                        <div className="grid grid-cols-7 auto-rows-fr min-h-[300px]" role="grid" aria-labelledby="calendar-heading">
                          <div role="row" style={{ display: 'contents' }}>
                            {weekDays.map((d, i) => (
                              <div key={`whd-${i}`} className="py-2 text-xs font-bold text-school-blue text-center bg-gray-50 border-b border-gray-200" role="columnheader">
                                <div>{dayNamesShort[d.getDay()]}</div>
                                <div className="text-[10px] text-gray-500 font-normal">
                                  {d.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', { month: 'short' })} {d.getDate()}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div role="row" style={{ display: 'contents' }}>
                          {weekDays.map((day, index) => {
                            const dayEvents = getEventsForDay(day);
                            const isToday = day.toDateString() === new Date().toDateString();
                            const dateLabel = day.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                            return (
                              <div
                                key={index}
                                role="gridcell"
                                aria-label={`${dateLabel}${dayEvents.length > 0 ? `, ${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}` : ''}`}
                                className={`border-t border-l p-2 sm:p-3 ${index % 7 === 6 ? 'border-r' : ''} ${
                                  isToday ? 'bg-school-gold/10 ring-2 ring-inset ring-school-gold' : 'bg-white'
                                }`}
                              >
                                <div className={`text-right mb-2 text-xs ${isToday ? 'font-bold text-school-blue' : 'text-gray-600'}`}>{day.getDate()}</div>
                                <div className="space-y-2 overflow-y-auto max-h-[240px]">
                                  {dayEvents.length === 0 && (
                                    <div className="text-[11px] text-gray-600 italic">
                                      {language === 'es' ? 'Sin eventos' : 'No events'}
                                    </div>
                                  )}
                                  {dayEvents.map((event) => (
                                    <button
                                      key={event.id}
                                      onClick={(e) => openEventModal(event, e.currentTarget)}
                                      className={`text-[11px] w-full text-left px-2 min-h-[24px] py-1 rounded ${categoryColors[event.category]} ${categoryTextColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-school-blue`}
                                      title={getEventTitle(event)}
                                      aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}`}
                                    >
                                      <span className="truncate block">{getEventTitle(event)}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* ===== LIST VIEW ===== */}
                  {currentView === 'list' && (
                    <div className="border-b border-gray-200 min-h-[520px] p-4">
                      {(() => {
                        const monthEvents = getFilteredEvents()
                          .filter((event) => {
                            const start = new Date(event.date);
                            const end = event.endDate ? new Date(event.endDate) : start;
                            const monthStart = new Date(currentYear, currentMonth, 1);
                            const monthEnd = new Date(currentYear, currentMonth + 1, 0);
                            return start <= monthEnd && end >= monthStart;
                          })
                          .sort((a, b) => a.date - b.date);

                        // No role="list" on the empty state: a list whose only child is a
                        // "no events" message has no listitem children to own.
                        if (monthEvents.length === 0) {
                          return (
                            <div className="text-center py-16 text-gray-600">
                              <CalendarDaysIcon className="w-12 h-12 mx-auto mb-3 text-gray-600" aria-hidden="true" />
                              <p className="text-sm font-medium">
                                {language === 'es'
                                  ? 'No hay eventos este mes según tus filtros.'
                                  : 'No events this month based on your filters.'}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-3" role="list" aria-label={`Events for ${getMonthLabel()}`}>
                            {monthEvents.map((event) => (
                              // listitem lives on the wrapper so the button keeps its button role.
                              <div key={event.id} role="listitem">
                                <button
                                  onClick={(e) => openEventModal(event, e.currentTarget)}
                                  className="w-full text-left flex border-l-4 p-3 min-h-[44px] bg-gray-50 rounded-r-xl hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-school-blue/40"
                                  style={{ borderColor: categoryColorValues[event.category] }}
                                  aria-label={`${getEventTitle(event)}, ${event.endDate ? formatDateRange(event) : formatDate(event.date)}, ${language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}`}
                                >
                                  <div className="mr-4 text-center flex-shrink-0 w-10">
                                    <div className="font-bold text-sm text-school-blue">
                                      {event.endDate
                                        ? `${event.date.getDate()}\u2013${new Date(event.endDate).getDate()}`
                                        : event.date.getDate()}
                                    </div>
                                    <div className="text-[10px] text-gray-500">
                                      {event.date.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', { month: 'short' })}
                                    </div>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-bold text-gray-900">{getEventTitle(event)}</div>
                                    <div className="text-[11px] text-gray-600">{getTimeDisplay(event)}</div>
                                    <div className="mt-1 text-[11px] text-gray-500 line-clamp-2">{getEventDescription(event)}</div>
                                    <div className="mt-2">
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                                          event.category === 'holiday' ? 'bg-red-100 text-red-800' :
                                          event.category === 'academic' ? 'bg-school-blue/10 text-school-blue' :
                                          event.category === 'community' ? 'bg-teal-100 text-teal-800' :
                                          event.category === 'spirit' ? 'bg-school-gold/20 text-school-blue' :
                                          'bg-purple-100 text-purple-800'
                                        }`}
                                      >
                                        {language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}
                                      </span>
                                    </div>
                                  </div>
                                </button>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* ==================== MODAL ==================== */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="modal-title" ref={modalRef}>
          <div className="flex items-end justify-center min-h-screen px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" aria-hidden="true" onClick={closeModal} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className={`${categoryColors[selectedEvent.category]} py-6 px-8`}>
                <h2 id="modal-title" className={`text-xl font-heading font-bold ${categoryTextColors[selectedEvent.category]}`}>
                  {getEventTitle(selectedEvent)}
                </h2>
                <p className={`${categoryTextColors[selectedEvent.category]} opacity-90 mt-1 text-sm`}>
                  {selectedEvent.endDate ? formatDateRange(selectedEvent) : formatDate(selectedEvent.date)}
                </p>
              </div>
              <div className="bg-white px-8 py-6">
                <div className="text-gray-700 text-sm">
                  <p className="mb-3 leading-relaxed">{getEventDescription(selectedEvent)}</p>
                  {(selectedEvent.startTime || selectedEvent.endTime || selectedEvent.allDay) && (
                    <div className="flex items-center mb-2 text-xs text-gray-600">
                      <svg className="h-4 w-4 text-school-gold mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3a.75.75 0 011.5 0v1.5h.75A2.25 2.25 0 0121 6.75v10.5A2.25 2.25 0 0118.75 19.5H5.25A2.25 2.25 0 013 17.25V6.75A2.25 2.25 0 015.25 4.5H6V3a.75.75 0 01.75-.75zM4.5 9h15v8.25a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9z" clipRule="evenodd" />
                      </svg>
                      <span>{getTimeDisplay(selectedEvent)}</span>
                    </div>
                  )}
                  {selectedEvent.location && (
                    <div className="flex items-center mb-2 text-xs text-gray-600">
                      <svg className="h-4 w-4 text-school-gold mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  <div className="mt-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                        selectedEvent.category === 'holiday' ? 'bg-red-100 text-red-800' :
                        selectedEvent.category === 'academic' ? 'bg-school-blue/10 text-school-blue' :
                        selectedEvent.category === 'community' ? 'bg-teal-100 text-teal-800' :
                        selectedEvent.category === 'spirit' ? 'bg-school-gold/20 text-school-blue' :
                        'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {language === 'es' ? getCategoryNameES(selectedEvent.category) : getCategoryName(selectedEvent.category)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-8 py-4 flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-6 py-2.5 min-h-[40px] bg-school-blue text-white rounded-full text-xs font-bold hover:bg-[#0F2240] focus:outline-none focus:ring-2 focus:ring-school-blue/40 transition-colors"
                >
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Calendar;
